import { motion } from 'framer-motion'
import { GitBranch, Target, Zap, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'

const solutions = [
    {
        icon: GitBranch,
        name: 'Nexus Flow Engine™',
        tagline: 'Agent Orchestration',
        description:
            'Visual interface to design, test, and deploy complex, multi-step Agents based on leading frameworks like LangGraph and AutoGen.',
        features: [
            'Visual workflow designer',
            'Multi-framework support (LangGraph, AutoGen)',
            'One-click deployment',
            'Real-time monitoring & debugging',
        ],
        gradient: 'from-primary-500 to-blue-500',
        featured: false,
    },
    {
        icon: Target,
        name: 'Vertex Retrieval Unit',
        tagline: 'High-Performance RAG',
        description:
            'Integrates Vector Databases with advanced Prompt Engineering (RIO principles) to optimize retrieval speed and accuracy, ensuring traceable and grounded content.',
        features: [
            'Vector database optimization',
            'Advanced prompt engineering',
            'Content traceability & validation',
            'Sub-second retrieval latency',
        ],
        gradient: 'from-secondary-500 to-purple-500',
        featured: true,
    },
    {
        icon: Zap,
        name: 'Apex Speed Core',
        tagline: 'Inference Optimization',
        description:
            'Inference acceleration engine deeply optimized with CUDA and TensorRT. Supports model quantization (INT8) and layer fusion, achieving industry-leading LLM inference latency.',
        features: [
            'CUDA/TensorRT optimization',
            'INT8 quantization & layer fusion',
            'Industry-leading low latency',
            'Multi-GPU scaling',
        ],
        gradient: 'from-accent-500 to-pink-500',
        featured: false,
    },
]

export const Solutions = () => {
    return (
        <section className="py-24 relative overflow-hidden">
            {/* Background Elements */}
            <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 -z-10" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary-900/10 via-transparent to-transparent -z-10" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl md:text-5xl font-bold mb-4">
                        <span className="gradient-text">AgentForge Platform</span>
                    </h2>
                    <p className="text-xl text-slate-400 max-w-3xl mx-auto">
                        End-to-end LLMOps solution focused on Agent and RAG system lifecycle management
                    </p>
                </motion.div>

                {/* Solutions Grid */}
                <div className="grid lg:grid-cols-3 gap-8 mb-12">
                    {solutions.map((solution, index) => {
                        const Icon = solution.icon
                        return (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                className="group relative"
                            >
                                {/* Card */}
                                <div
                                    className={`h-full p-8 rounded-2xl bg-slate-800/50 border transition-all duration-300 backdrop-blur-sm ${solution.featured
                                            ? 'border-primary-500/50 shadow-lg shadow-primary-500/20'
                                            : 'border-slate-700/50 hover:border-slate-600'
                                        }`}
                                >
                                    {/* Featured Badge */}
                                    {solution.featured && (
                                        <div className="absolute -top-3 right-8 px-4 py-1 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full text-xs font-semibold">
                                            Most Popular
                                        </div>
                                    )}

                                    {/* Icon */}
                                    <div
                                        className={`w-16 h-16 rounded-xl bg-gradient-to-br ${solution.gradient} flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300`}
                                    >
                                        <Icon className="w-8 h-8 text-white" />
                                    </div>

                                    {/* Header */}
                                    <div className="mb-4">
                                        <h3 className="text-2xl font-bold mb-2 text-white">
                                            {solution.name}
                                        </h3>
                                        <div
                                            className={`inline-block px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${solution.gradient} bg-clip-text text-transparent border border-slate-700`}
                                        >
                                            {solution.tagline}
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <p className="text-slate-400 mb-6 leading-relaxed">
                                        {solution.description}
                                    </p>

                                    {/* Features */}
                                    <ul className="space-y-3 mb-6">
                                        {solution.features.map((feature, fIndex) => (
                                            <li
                                                key={fIndex}
                                                className="flex items-start space-x-2 text-sm text-slate-300"
                                            >
                                                <svg
                                                    className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M5 13l4 4L19 7"
                                                    />
                                                </svg>
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    {/* Learn More Link */}
                                    <Link
                                        to="/platform"
                                        className={`inline-flex items-center space-x-2 text-sm font-semibold bg-gradient-to-r ${solution.gradient} bg-clip-text text-transparent group-hover:translate-x-1 transition-transform duration-300`}
                                    >
                                        <span>Learn more</span>
                                        <ArrowRight className="w-4 h-4" />
                                    </Link>
                                </div>
                            </motion.div>
                        )
                    })}
                </div>

                {/* CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center"
                >
                    <Link
                        to="/platform"
                        className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 rounded-xl font-semibold text-lg transition-all duration-300 shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40"
                    >
                        <span>Explore All Features</span>
                        <ArrowRight className="w-5 h-5" />
                    </Link>
                </motion.div>
            </div>
        </section>
    )
}

